import type { DvaInstance } from 'dva';
import type { History } from 'history';
import React, { Suspense } from 'react';
import { routerRedux } from 'dva';
import { Switch, Route, Redirect } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { CatchAllErrorBoundary, ErrorPage, Loader } from '@helloworld1812/app-global-components';
import { useWsGrowthbook } from '@helloworld1812/ws-growthbook';
import { Box } from '@helloworld1812/ws-components';

import { routePath } from '@/helpers/route';
import { FullHeightWrapperWithLoader } from '@/components/common/full-height-wrapper-with-loader';
import MultitabDetector from '@/components/app/multitab-detector';

import HiringPaywallPage from '../routes/hiring-paywall';

import { NonBusinessApp } from './non-business-app';
import UnauthorizedApp from './unauthorized-app';
import AuthorizedApp from './authorized-app';

const { ConnectedRouter } = routerRedux;

const Dashboard = React.lazy(() => import(/* webpackChunkName: "routes--dashboard" */ '../routes/dashboard'));
const AdminDashboard = React.lazy(
  () => import(/* webpackChunkName: "routes--admin-dashboard" */ '../routes/company/dashboard')
);
const SADashboard = React.lazy(() => import(/* webpackChunkName: "routes--sa-dashboard" */ '../routes/sa-dashboard'));
const AccountRoutes = React.lazy(() => import(/* webpackChunkName: "routes--account" */ '../routes/account'));
const MyProfile = React.lazy(() => import(/* webpackChunkName: "routes--my-profile" */ '../routes/my-profile'));
const Company = React.lazy(() => import(/* webpackChunkName: "routes--company" */ '../routes/company'));
const Referral = React.lazy(() => import(/* webpackChunkName: "routes--referral" */ '../routes/company/referral'));
const LoginPage = React.lazy(() => import(/* webpackChunkName: "routes--login" */ '../routes/login'));
const SuccessLoginPage = React.lazy(
  () => import(/* webpackChunkName: "routes--success-login" */ '../routes/success-login')
);
const SetPasswordPage = React.lazy(
  () => import(/* webpackChunkName: "routes--setup--password" */ '../routes/set-password/[token]')
);
const ForgotPasswordPage = React.lazy(
  () => import(/* webpackChunkName: "routes--forgot-password" */ '../routes/forgot-password')
);
const ResetPasswordPage = React.lazy(
  () => import(/* webpackChunkName: "routes--reset-password" */ '../routes/reset-password/[token]')
);

const JobPostingTemplateDetail = React.lazy(async () => {
  const { PositionTemplateDetail } = await import(
    /* webpackChunkName: "routes--position-template-detail" */ '../routes/decoupled-job-posting/position/position-template-detail'
  );
  return { default: PositionTemplateDetail };
});

const JobPostings = React.lazy(async () => {
  const { JobPostingIndexPage } = await import(
    /* webpackChunkName: "routes--job-posting-index-page" */ '../routes/decoupled-job-posting/job-posting/job-posting-index-page'
  );
  return { default: JobPostingIndexPage };
});

const JobPostingDetail = React.lazy(async () => {
  const { JobPostingDetailPage } = await import(
    /* webpackChunkName: "routes--job-posting-detail" */ '../routes/decoupled-job-posting/job-posting/job-posting-detail-page'
  );
  return { default: JobPostingDetailPage };
});

const HiringProcessTemplateCreateRoute = React.lazy(
  () =>
    import(
      /* webpackChunkName: "routes--create-hiring-process-template" */ '../routes/hiring-process-template-create-route'
    )
);

const HiringProcessTemplateEditRoute = HiringProcessTemplateCreateRoute;

const ScheduleRoute = React.lazy(() => import(/* webpackChunkName: "routes--schedule--Entry" */ '../routes/schedule'));

const JobPostingApplicantsRoute = React.lazy(
  () =>
    import(
      /* webpackChunkName: "application-routes--job-posting-applicants" */ '../routes/applicants/[location]/[position]/[digestKey]'
    )
);

const ActiveApplicantsRoute = React.lazy(
  () => import(/* webpackChunkName: "application-routes--applicants" */ '../routes/applicants')
);

const InactiveApplicantsRoute = React.lazy(
  () =>
    import(/* webpackChunkName: "application-routes--inactive-applicants" */ '../routes/applicants/inactive-applicants')
);

const ApplicantDetailPage = React.lazy(
  () => import(/* webpackChunkName: "application-routes--applicant-detail-page" */ '../routes/applicants/[id]')
);

const RegistCheckr = React.lazy(
  () => import(/* webpackChunkName: "routes--integration--RegistCheckr" */ '../routes/integration/regist-checkr')
);
const Messages = React.lazy(() => import(/* webpackChunkName: "routes--messages" */ '../routes/messages'));

const UserOnBoarding = React.lazy(
  () => import(/* webpackChunkName: "routes--user-onboarding" */ '../routes/user-onboarding')
);
const EmployeeOnboarding = React.lazy(
  () => import(/* webpackChunkName: "routes--employee-onboarding" */ '../routes/employee-onboarding')
);
const EmployeeOnboardingTasks = React.lazy(
  () =>
    import(
      /* webpackChunkName: "routes--employee-onboarding--view-onboarding" */ '../routes/employee-onboarding/view-onboarding'
    )
);
const StandaloneStartOnboarding = React.lazy(async () => {
  const { StandaloneStartOnboarding: StandaloneStartOnboardingComp } = await import(
    /* webpackChunkName: "routes--employee-onboarding--standalone-start-onboarding" */ '../routes/employee-onboarding/standalone-start-onboarding'
  );
  return { default: StandaloneStartOnboardingComp };
});
const StandaloneChangeOnboardingProcess = React.lazy(async () => {
  const { StandaloneChangeOnboardingProcess: StandaloneChangeOnboardingProcessComp } = await import(
    /* webpackChunkName: "routes--employee-onboarding--standalone-change-onboarding-process" */ '../routes/employee-onboarding/standalone-change-onboarding-process'
  );
  return { default: StandaloneChangeOnboardingProcessComp };
});
const ADP = React.lazy(() => import(/* webpackChunkName: "routes--sso--adp" */ '../routes/sso/adp'));
const ADPRedirect = React.lazy(
  () => import(/* webpackChunkName: "routes--sso--adp--redirect" */ '../routes/sso/adp/redirect')
);
const JobTargetCallback = React.lazy(
  () => import(/* webpackChunkName: "routes--job-target--Callback" */ '../routes/job-target/Callback')
);
const RedirectPage = React.lazy(() => import(/* webpackChunkName: "routes--redirect" */ '../routes/redirect'));
const SelectCompany = React.lazy(
  () => import(/* webpackChunkName: "routes--select-company" */ '../routes/select-company')
);

const SourcingToolsPage = React.lazy(() => import('../routes/sourcing-tools'));
const Support = React.lazy(() => import('../routes/support'));
const PayrollPaywall = React.lazy(
  () => import(/* webpackChunkName: "routes--ErrorPage" */ '../routes/payroll-paywall')
);

const Analytics = React.lazy(() => import(/* webpackChunkName: "routes--analytics" */ '../routes/analytics'));

const HomeRedirect = () => {
  const { ready, instance } = useWsGrowthbook();
  if (!ready) {
    return null;
  }
  if (instance?.isOn('homepage-redesign-v1')) {
    return <Redirect to="/new-home" />;
  }
  return <Redirect to="/dashboard" />;
};

function Router({ history }: { app: DvaInstance; history: History }) {
  return (
    <NonBusinessApp>
      <CatchAllErrorBoundary>
        <Suspense fallback={<FullHeightWrapperWithLoader spinning />}>
          <UnauthorizedApp>
            <Switch>
              <Route exact path="/redirect" component={RedirectPage} />
              <Route exact path="/success-login" component={SuccessLoginPage} />
              <Route exact path="/login" component={LoginPage} />
              <Route exact path="/forgot-password" component={ForgotPasswordPage} />
              <Route exact path="/set-password/:token" component={SetPasswordPage} />
              <Route exact path="/reset-password/:token" component={ResetPasswordPage} />
              <Route exact path="/job-target-callback" component={JobTargetCallback} />
              <Route exact path="/select-company" component={SelectCompany} />

              {/* Any route requires auth should be under UnauthorizedApp */}
              <Route path="/">
                <Suspense
                  fallback={
                    <Box height="100%" width="100%">
                      <Loader spinning />
                    </Box>
                  }
                >
                  <AuthorizedApp noPaddingRoutes={['/new-home']}>
                    <Suspense
                      fallback={
                        <Box height="100%" width="100%">
                          <Loader spinning />
                        </Box>
                      }
                    >
                      <Switch>
                        <Route exact path="/" component={HomeRedirect} />
                        <Route exact path="/user-onboarding" component={UserOnBoarding} />
                        <Route exact path="/dashboard" component={Dashboard} />
                        <Route
                          exact
                          path="/new-home"
                          render={() => {
                            window.history.replaceState(null, '', '/hiring/new-home');
                            return null;
                          }}
                        />
                        <Route exact path="/dashboard/:brand" component={SADashboard} />
                        <Route exact path="/positions" render={() => <Redirect to="/job-postings" />} />
                        <Route
                          exact
                          path="/job-posting-template/:position_template_id"
                          component={JobPostingTemplateDetail}
                        />
                        <Route exact path="/job-postings" component={JobPostings} />
                        <Route exact path="/job-postings/:digestkey" component={JobPostingDetail} />
                        <Route
                          exact
                          path={routePath.hiringProcessTemplate.createHiringProcessTemplate.router()}
                          component={HiringProcessTemplateCreateRoute}
                        />
                        <Route
                          exact
                          path={routePath.hiringProcessTemplate.editHiringProcessTemplate.router()}
                          component={HiringProcessTemplateEditRoute}
                        />
                        <Route
                          exact
                          path="/applicants/:location/:position/:digestkey"
                          component={JobPostingApplicantsRoute}
                        />
                        <Route exact path="/applicants" component={ActiveApplicantsRoute} />
                        <Route exact path="/applicants/all-applicants" component={ActiveApplicantsRoute} />
                        <Route exact path="/applicants/inactive-applicants" component={InactiveApplicantsRoute} />
                        <Route exact path="/messages" component={Messages} />
                        <Route path="/applicant/:id" component={ApplicantDetailPage} />
                        <Route exact path="/my-profile" component={MyProfile} />
                        <Route exact path="/account" component={AccountRoutes} />
                        <Route path="/account/:panel" component={AccountRoutes} />
                        <Route exact path="/account/:id" component={AccountRoutes} />
                        <Route exact path="/company" component={Company} />
                        <Route exact path="/company/dashboard" component={AdminDashboard} />
                        <Route exact path="/referral" component={Referral} />
                        <Route path="/analytics" component={Analytics} />
                        <Route exact path="/integration/checkr" component={RegistCheckr} />
                        <Route exact path="/schedule" component={ScheduleRoute} />
                        <Redirect
                          exact
                          from="/onboarding/processes"
                          to={{
                            pathname: '/admin/onboarding/processes',
                            search: history?.location?.search,
                          }}
                        />
                        <Route
                          exact
                          path={routePath.employeeOnboarding.standaloneOnboardingTasks.routeMatchPath}
                          component={EmployeeOnboardingTasks}
                        />
                        <Route
                          exact
                          path={routePath.employeeOnboarding.standaloneStartOnboarding}
                          component={StandaloneStartOnboarding}
                        />
                        <Route
                          exact
                          path={routePath.employeeOnboarding.standaloneChangeOnboardingProcess}
                          component={StandaloneChangeOnboardingProcess}
                        />
                        <Route exact path="/onboarding" component={EmployeeOnboarding} />
                        <Route exact path="/admin/onboarding" component={EmployeeOnboarding} />
                        <Route path="/admin/onboarding/:page" component={EmployeeOnboarding} />
                        <Route path="/onboarding/:page" component={EmployeeOnboarding} />

                        <Redirect
                          from="/employee-onboarding"
                          to={{
                            pathname: '/onboarding',
                            search: history?.location?.search,
                          }}
                        />
                        <Redirect
                          from="/employee-onboarding/:page"
                          to={{
                            pathname: '/onboarding/:page',
                            search: history?.location?.search,
                          }}
                        />
                        <Route exact path="/sso/adp" component={ADP} />
                        <Route exact path="/adp/callback" component={ADP} />
                        <Route exact path="/adp/redirect" component={ADPRedirect} />
                        <Route exact path="/sourcing-tools" component={SourcingToolsPage} />
                        <Route exact path="/support" component={Support} />
                        <Route exact path="/payroll-paywall" component={PayrollPaywall} />
                        <Route exact path="/hiring-paywall" component={HiringPaywallPage} />

                        <Route component={ErrorPage} />
                      </Switch>
                    </Suspense>
                  </AuthorizedApp>
                </Suspense>
              </Route>
            </Switch>

            <MultitabDetector />
          </UnauthorizedApp>
        </Suspense>
      </CatchAllErrorBoundary>
    </NonBusinessApp>
  );
}

export default function DvaRouter(props: { app: DvaInstance; history: History }) {
  const { history } = props;

  return (
    <ConnectedRouter history={history}>
      <CompatRouter>
        <Router history={history} />
      </CompatRouter>
    </ConnectedRouter>
  );
}
